<template>
  <v-dialog
    v-model="dialog"
    max-width="760"
    scrollable
    content-class="rev-dialog"
    transition="dialog-bottom-transition"
  >
    <div class="rev-card">
      <!-- ===== Header ===== -->
      <div class="rev-header">
        <span class="rev-header__decor rev-header__decor--1"></span>
        <span class="rev-header__decor rev-header__decor--2"></span>

        <div class="rev-header__bar">
          <div class="rev-header__icon">
            <v-icon size="22" color="#fff">mdi-calendar-star</v-icon>
          </div>
          <div class="rev-header__text">
            <h3 class="rev-header__title">Active Events</h3>
          </div>
          <button
            class="rev-ghost-btn"
            :disabled="loading"
            title="Refresh"
            @click="loadEvents()"
          >
            <v-icon size="18" color="#fff" :class="{ 'rev-spin': loading }">mdi-refresh</v-icon>
          </button>
          <button class="rev-ghost-btn" title="Close" @click="dialog = false">
            <v-icon size="18" color="#fff">mdi-close</v-icon>
          </button>
        </div>

        <!-- Stat chips -->
        <div class="rev-stats">
          <div class="rev-stat">
            <span class="rev-stat__num">{{ totalRunning }}</span>
            <span class="rev-stat__label">Active event{{ totalRunning === 1 ? '' : 's' }}</span>
          </div>
          <div class="rev-stat">
            <span class="rev-stat__num">{{ activeModuleCount }}</span>
            <span class="rev-stat__label">Module{{ activeModuleCount === 1 ? '' : 's' }} live</span>
          </div>
          <div class="rev-stat rev-stat--muted">
            <span class="rev-stat__num">{{ groups.length }}</span>
            <span class="rev-stat__label">Total module{{ groups.length === 1 ? '' : 's' }}</span>
          </div>
        </div>
      </div>

      <!-- ===== Tabs + sub-toolbar ===== -->
      <div class="rev-toolbar" v-if="!loading && !error && groups.length">
        <div class="rev-tabs">
          <button
            class="rev-tab"
            :class="{ 'rev-tab--active': view === 'calendar' }"
            @click="view = 'calendar'"
          >
            <v-icon
              size="16"
              :color="view === 'calendar' ? 'var(--app-accent-fg)' : 'var(--app-text-muted)'"
            >mdi-calendar-month-outline</v-icon>
            Calendar
          </button>
          <button
            class="rev-tab"
            :class="{ 'rev-tab--active': view === 'list' }"
            @click="view = 'list'"
          >
            <v-icon
              size="16"
              :color="view === 'list' ? 'var(--app-accent-fg)' : 'var(--app-text-muted)'"
            >mdi-format-list-bulleted</v-icon>
            List
          </button>
        </div>

        <!-- List view: search -->
        <div v-if="view === 'list'" class="rev-search">
          <v-icon size="18" color="var(--app-text-faint)">mdi-magnify</v-icon>
          <input
            v-model="search"
            type="text"
            class="rev-search__input"
            placeholder="Search a module or event…"
          />
          <button
            v-if="search"
            class="rev-search__clear"
            title="Clear"
            @click="search = ''"
          >
            <v-icon size="16" color="var(--app-text-faint)">mdi-close-circle</v-icon>
          </button>
        </div>

        <!-- Calendar view: month navigation -->
        <div v-else class="rev-cal-nav">
          <button class="rev-cal-nav__btn" title="Previous month" @click="calPrev">
            <v-icon size="18" color="var(--app-text-muted)">mdi-chevron-left</v-icon>
          </button>
          <span class="rev-cal-nav__title">{{ calendarTitle }}</span>
          <button class="rev-cal-nav__btn" title="Next month" @click="calNext">
            <v-icon size="18" color="var(--app-text-muted)">mdi-chevron-right</v-icon>
          </button>
          <button class="rev-cal-today" @click="setToday">Today</button>
        </div>
      </div>

      <!-- ===== Body ===== -->
      <div class="rev-body">
        <!-- Loading skeleton -->
        <div v-if="loading" class="rev-loading">
          <div class="rev-skel-card" v-for="n in 3" :key="n">
            <div class="rev-skel-row">
              <div class="rev-skel rev-skel--chip"></div>
              <div class="rev-skel rev-skel--title"></div>
            </div>
            <div class="rev-skel rev-skel--line"></div>
            <div class="rev-skel rev-skel--line rev-skel--short"></div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="rev-state rev-state--error">
          <div class="rev-state__icon"><v-icon size="32" color="var(--app-danger)">mdi-alert-circle-outline</v-icon></div>
          <span class="rev-state__title">Couldn't load events</span>
          <span class="rev-state__text">{{ error }}</span>
          <button class="rev-btn rev-btn--primary" @click="loadEvents()">Try again</button>
        </div>

        <!-- No modules at all -->
        <div v-else-if="!groups.length" class="rev-state">
          <div class="rev-state__icon"><v-icon size="32" color="var(--app-text-faint)">mdi-calendar-remove-outline</v-icon></div>
          <span class="rev-state__title">No event modules available</span>
          <span class="rev-state__text">Event modules will appear here once configured.</span>
        </div>

        <!-- ===== LIST VIEW ===== -->
        <template v-else-if="view === 'list'">
          <!-- No search results -->
          <div v-if="!filteredGroups.length" class="rev-state">
            <div class="rev-state__icon"><v-icon size="32" color="var(--app-text-faint)">mdi-magnify-close</v-icon></div>
            <span class="rev-state__title">No matches</span>
            <span class="rev-state__text">Nothing matches “{{ search }}”.</span>
          </div>

          <!-- Module cards -->
          <template v-else>
            <div
              class="rev-module"
              :class="{ 'rev-module--idle': !group.events.length }"
              :style="{ '--accent': group.color }"
              v-for="group in filteredGroups"
              :key="group.serviceKey"
            >
              <div class="rev-module__head">
                <div
                  class="rev-module__icon"
                  :style="group.events.length ? { background: group.color + '1a' } : null"
                >
                  <v-icon size="17" :color="group.events.length ? group.color : 'var(--app-text-faint)'">
                    {{ group.icon }}
                  </v-icon>
                </div>
                <span class="rev-module__name">{{ group.name }}</span>
                <span
                  class="rev-module__count"
                  :class="group.events.length ? 'rev-module__count--on' : 'rev-module__count--off'"
                >
                  {{ group.events.length }} active
                </span>
              </div>

              <div v-if="group.events.length" class="rev-events">
                <div class="rev-event" v-for="(ev, i) in group.events" :key="i">
                  <span class="rev-event__title">{{ ev.title }}</span>
                  <span class="rev-event__dates">
                    <v-icon size="13" color="var(--app-text-faint)">mdi-calendar-blank-outline</v-icon>
                    {{ formatDate(ev.startYmd) }}
                    <v-icon size="12" color="var(--app-border-strong)">mdi-arrow-right</v-icon>
                    {{ formatDate(ev.endYmd) }}
                  </span>
                </div>
              </div>

              <div v-else class="rev-module__idle">
                No active events
              </div>
            </div>
          </template>
        </template>

        <!-- ===== CALENDAR VIEW ===== -->
        <div v-else class="rev-cal-wrap">
          <v-calendar
            ref="calendar"
            v-model="focus"
            type="month"
            color="var(--app-accent)"
            :events="calendarEvents"
            :event-color="getEventColor"
            event-text-color="#ffffff"
            @click:event="showEvent"
            @click:more="showMore"
          ></v-calendar>

          <!-- Event detail popover -->
          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            offset-y
          >
            <div class="rev-evt-pop" v-if="selectedEvent">
              <div class="rev-evt-pop__bar" :style="{ background: selectedEvent.color }"></div>
              <div class="rev-evt-pop__main">
                <span class="rev-evt-pop__module">
                  <v-icon size="13" :color="selectedEvent.color">{{ selectedEvent.icon }}</v-icon>
                  {{ selectedEvent.module }}
                </span>
                <span class="rev-evt-pop__title">{{ selectedEvent.name }}</span>
                <span class="rev-evt-pop__dates">
                  <v-icon size="13" color="var(--app-text-faint)">mdi-calendar-blank-outline</v-icon>
                  {{ formatDate(selectedEvent.start) }}
                  <v-icon size="12" color="var(--app-border-strong)">mdi-arrow-right</v-icon>
                  {{ formatDate(selectedEvent.end) }}
                </span>
              </div>
            </div>
          </v-menu>

          <!-- "X more" popover: lists every event on the clicked day -->
          <v-menu
            v-model="moreOpen"
            :close-on-content-click="false"
            :activator="moreElement"
            offset-y
          >
            <div class="rev-more-pop" v-if="moreDate">
              <div class="rev-more-pop__head">
                <v-icon size="14" color="var(--app-accent)">mdi-calendar-blank-outline</v-icon>
                {{ formatDate(moreDate) }}
              </div>
              <div class="rev-more-pop__list">
                <div
                  class="rev-more-pop__item"
                  v-for="(ev, i) in moreEvents"
                  :key="i"
                >
                  <span class="rev-more-pop__bar" :style="{ background: ev.color }"></span>
                  <div class="rev-more-pop__main">
                    <span class="rev-more-pop__module">
                      <v-icon size="12" :color="ev.color">{{ ev.icon }}</v-icon>
                      {{ ev.module }}
                    </span>
                    <span class="rev-more-pop__title">{{ ev.name }}</span>
                    <span class="rev-more-pop__dates">
                      <v-icon size="12" color="var(--app-text-faint)">mdi-calendar-blank-outline</v-icon>
                      {{ formatDate(ev.start) }}
                      <v-icon size="11" color="var(--app-border-strong)">mdi-arrow-right</v-icon>
                      {{ formatDate(ev.end) }}
                    </span>
                  </div>
                </div>
                <div v-if="!moreEvents.length" class="rev-more-pop__empty">
                  No events on this day
                </div>
              </div>
            </div>
          </v-menu>
        </div>
      </div>

      <!-- ===== Footer ===== -->
      <div class="rev-footer">
        <span class="rev-footer__hint" v-if="!loading && !error">
          <!-- <v-icon size="14" color="#cbd5e1">mdi-information-outline</v-icon>
          Showing every event marked Active -->
        </span>
        <button class="rev-btn rev-btn--primary" @click="dialog = false">Close</button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import { getAllEventConfigs } from "@/services/hrdc_event/eventRegistry";

export default {
  name: "DashboardRunningEventsDialog",
  data: () => ({
    dialog: false,
    loading: false,
    loaded: false,
    error: null,
    search: "",
    groups: [],
    // 'calendar' | 'list'
    view: "calendar",
    // v-calendar focus date (YYYY-MM-DD); empty -> current month/day.
    focus: "",
    selectedOpen: false,
    selectedEvent: null,
    selectedElement: null,
    // "X more" day popover state.
    moreOpen: false,
    moreDate: null,
    moreElement: null,
  }),
  computed: {
    totalRunning() {
      return this.groups.reduce((sum, g) => sum + g.events.length, 0);
    },
    activeModuleCount() {
      return this.groups.filter((g) => g.events.length).length;
    },
    filteredGroups() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.groups;
      return this.groups
        .map((g) => {
          const nameMatch = g.name.toLowerCase().includes(q);
          const events = nameMatch
            ? g.events
            : g.events.filter((e) =>
                (e.title || "").toLowerCase().includes(q)
              );
          return { ...g, events, _hit: nameMatch || events.length > 0 };
        })
        .filter((g) => g._hit);
    },
    // Flatten every module's active events into v-calendar's event shape.
    calendarEvents() {
      const out = [];
      for (const g of this.groups) {
        for (const ev of g.events) {
          if (!ev.startYmd) continue;
          out.push({
            name: ev.title,
            start: ev.startYmd,
            end: ev.endYmd || ev.startYmd,
            color: g.color,
            module: g.name,
            icon: g.icon,
          });
        }
      }
      return out;
    },
    // Every event overlapping the day whose "X more" link was clicked.
    // start/end are YYYY-MM-DD strings, so lexical comparison is date-correct.
    moreEvents() {
      if (!this.moreDate) return [];
      const d = this.moreDate;
      return this.calendarEvents
        .filter((ev) => ev.start <= d && (ev.end || ev.start) >= d)
        .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    },
    // Month + year label for the calendar toolbar (timezone-safe parse).
    calendarTitle() {
      let y;
      let m;
      if (/^\d{4}-\d{2}-\d{2}/.test(this.focus)) {
        const p = this.focus.split("-");
        y = Number(p[0]);
        m = Number(p[1]) - 1;
      } else {
        const now = new Date();
        y = now.getFullYear();
        m = now.getMonth();
      }
      const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      return `${months[m]} ${y}`;
    },
  },
  mounted() {
    // Lazily populate the trigger badge count without blocking the dashboard.
    this.loadEvents();
  },
  methods: {
    open() {
      this.dialog = true;
      this.search = "";
      this.view = "calendar";
      this.setToday();
      // Always refresh on open — events are dynamic and user-created.
      this.loadEvents();
    },
    setToday() {
      // Today's local date as YYYY-MM-DD -> calendar shows current month + day.
      const offset = new Date().getTimezoneOffset() * 60000;
      this.focus = new Date(Date.now() - offset).toISOString().slice(0, 10);
    },
    calPrev() {
      if (this.$refs.calendar) this.$refs.calendar.prev();
    },
    calNext() {
      if (this.$refs.calendar) this.$refs.calendar.next();
    },
    getEventColor(event) {
      return event.color || "#0ea5e9";
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        );
      };
      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(open));
      } else {
        open();
      }
      nativeEvent.stopPropagation();
    },
    // v-calendar emits click:more with { nativeEvent, ...day }. Open a popover
    // listing every event on that day (the bug was: no handler -> nothing).
    showMore({ nativeEvent, date }) {
      const open = () => {
        this.moreDate = date;
        this.moreElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.moreOpen = true))
        );
      };
      // Don't let the single-event popover and this one fight over the screen.
      this.selectedOpen = false;
      if (this.moreOpen) {
        this.moreOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(open));
      } else {
        open();
      }
      nativeEvent.stopPropagation();
    },
    async loadEvents() {
      if (this.loading) return;
      this.loading = true;
      this.error = null;

      try {
        const configs = getAllEventConfigs();
        if (!configs.length) {
          this.groups = [];
          this.$emit("update:count", 0);
          return;
        }

        // ONE request for every module's active events. Omitting event_type
        // makes get-events return all types; active=true filters server-side.
        // All event modules share the same API host, so any config's URL works.
        const allEvents = await this.fetchAllActiveEvents(configs[0].serviceUrl);

        // Bucket the flat response by its numeric eventType.
        const byType = {};
        for (const e of allEvents) {
          if (!this.isActive(e)) continue;
          const key = String(e.eventType);
          (byType[key] = byType[key] || []).push(e);
        }

        // One group per active module (modules with none are shown greyed).
        const groups = configs.map((cfg) => {
          const events = (byType[String(cfg.hrdcType)] || [])
            .map((e) => ({
              title: e.title,
              startYmd: this.toYmd(e.startDate),
              endYmd: this.toYmd(e.endDate),
            }))
            .sort((a, b) => (a.startYmd || "").localeCompare(b.startYmd || ""));

          return {
            serviceKey: cfg.serviceKey,
            name: cfg.name1 || cfg.serviceKey,
            // Per-module icon + accent colour configured in the EventModule
            // settings; fall back to a neutral calendar / blue if unset.
            icon: cfg.icon || "mdi-calendar-text-outline",
            color: cfg.accentColor || "#0ea5e9",
            events,
          };
        });

        // Modules with active events first, then alphabetical.
        groups.sort((a, b) => {
          if (!!b.events.length !== !!a.events.length) {
            return b.events.length - a.events.length;
          }
          return a.name.localeCompare(b.name);
        });

        this.groups = groups;
        this.loaded = true;
        this.$emit("update:count", this.totalRunning);
      } catch (err) {
        this.error =
          (err && err.message) || "Unexpected error while loading events.";
      } finally {
        this.loading = false;
      }
    },
    fetchAllActiveEvents(serviceUrl) {
      return new Promise((resolve, reject) => {
        const api = new Api();
        api.setUrl(`${serviceUrl}/v1/en/console/settings/event/get-events`);
        api.setQuery("active=true");
        api.setCallbackCompleted((response) => {
          resolve(
            response && response.status && Array.isArray(response.data)
              ? response.data
              : []
          );
        });
        api.setCallbackError((err) =>
          reject(err instanceof Error ? err : new Error(String(err)))
        );
        api.fetch();
      });
    },
    isActive(event) {
      // Show every event flagged Active, regardless of its start/end dates.
      return !!(event && event.status);
    },
    toYmd(value) {
      if (!value) return null;
      const str = String(value);
      const m = str.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
      const d = new Date(str);
      if (isNaN(d.getTime())) return null;
      const offset = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - offset).toISOString().slice(0, 10);
    },
    formatDate(ymd) {
      if (!ymd) return "—";
      const parts = ymd.split("-");
      if (parts.length !== 3) return ymd;
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      return `${parseInt(parts[2], 10)} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored — do NOT regenerate this from the light rules below (that is
   what made the palette drift across 226 files before). Colours here are token
   references only (--app-*, defined in src/plugins/theme-dark.css).

   The light design is built on a local Tailwind-slate/blue scale declared on
   .rev-card, and every stop of it happens to be the light value of an --app-*
   token (--slate-200 = --app-border, --slate-500 = --app-text-muted,
   --blue-500 = --app-accent, …). So dark mode is mostly one re-point of that
   local scale; explicit rules are only needed where

     · one light value does two jobs — --slate-100 is both a hover fill and a
       hairline, and a hover fill used as a border on charcoal reads as the
       white-chalk outline the ramp is supposed to replace;
     · a solid accent fill carries literal white text (#fff on #38bdf8 fails
       contrast — those become --app-accent-fg);
     · the surface is painted with a literal #fff.

   Two things the local scale can't reach:
     · the v-menu popovers (.rev-evt-pop / .rev-more-pop) are teleported out of
       .rev-card, so --slate-* never resolves for them — their light rules are
       written against --app-* tokens directly and need no dark companion;
     · v-calendar paints its own dark theme (#303030 with #9e9e9e gridlines —
       chalk outlines again), so its internals are re-pointed at the surface
       ramp + --app-border below.
   ============================================================================ */

.dark-mode .rev-card {
  /* Re-point the local scale; everything below inherits from this. */
  --blue-400: var(--app-accent);
  --blue-500: var(--app-accent);
  --blue-600: var(--app-accent-strong);
  --blue-700: var(--app-accent-strong);
  --slate-50: var(--app-surface);
  --slate-100: var(--app-elevated);
  --slate-200: var(--app-border);
  --slate-400: var(--app-text-faint);
  --slate-500: var(--app-text-muted);
  --slate-700: var(--app-text);
  --slate-800: var(--app-text);

  background: var(--app-surface);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px -12px var(--app-shadow-deep);
}

/* Header keeps the brand gradient — the dialog's only saturated surface — but
   at the deeper dark ramp so it doesn't glare off a charcoal page. */
.dark-mode .rev-header {
  background: var(--app-brand-gradient);
}

/* ---- Toolbar / tabs ---- */
.dark-mode .rev-tabs {
  background: var(--app-card);
}

.dark-mode .rev-tab:hover {
  background: var(--app-hover);
}

.dark-mode .rev-tab--active,
.dark-mode .rev-tab--active:hover {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.dark-mode .rev-cal-nav__btn {
  background: var(--app-card);
}

.dark-mode .rev-cal-today {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
}

.dark-mode .rev-cal-today:hover {
  background: var(--app-accent-border);
}

.dark-mode .rev-search {
  background: var(--app-card);
}

.dark-mode .rev-search:focus-within {
  box-shadow: 0 0 0 3px var(--app-ring);
}

/* ---- Calendar ---- */
.dark-mode .rev-cal-wrap {
  background: var(--app-card);
}

.dark-mode .rev-cal-wrap >>> .v-calendar-weekly {
  background: var(--app-card);
  border-top-color: var(--app-border);
  border-left-color: var(--app-border);
}

.dark-mode .rev-cal-wrap >>> .v-calendar-weekly__head-weekday {
  border-color: var(--app-border);
  color: var(--app-text-faint);
}

.dark-mode .rev-cal-wrap >>> .v-calendar-weekly__day {
  border-color: var(--app-border);
  color: var(--app-text);
}

.dark-mode .rev-cal-wrap >>> .v-calendar-weekly__head-weekday.v-outside,
.dark-mode .rev-cal-wrap >>> .v-calendar-weekly__day.v-outside {
  background: var(--app-surface);
}

.dark-mode .rev-cal-wrap >>> .v-event-more {
  background: var(--app-card);
  color: var(--app-text-muted);
}

.dark-mode .rev-cal-wrap
  >>> .v-calendar-weekly__day.v-present
  .v-calendar-weekly__day-label
  .v-btn {
  color: var(--app-accent-fg) !important;
}

/* ---- Module cards ---- */
.dark-mode .rev-module {
  background: var(--app-card);
  box-shadow: 0 1px 2px var(--app-shadow-soft),
    inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .rev-module:not(.rev-module--idle):hover {
  box-shadow: 0 8px 24px -6px var(--app-shadow),
    inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .rev-module--idle {
  background: var(--app-surface);
}

.dark-mode .rev-module__icon {
  background: var(--app-accent-soft);
}

.dark-mode .rev-module__count--on {
  background: var(--app-success-bg);
  color: var(--app-success-fg);
}

/* Row separators inside a card need the palette border, not the hover fill
   --slate-100 maps to (it would be all but invisible on --app-card). */
.dark-mode .rev-events,
.dark-mode .rev-event {
  border-color: var(--app-border);
}

/* ---- States ---- */
.dark-mode .rev-state__icon {
  background: var(--app-card);
}

.dark-mode .rev-state--error .rev-state__icon {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
}

/* ---- Skeleton ---- */
.dark-mode .rev-skel-card {
  background: var(--app-card);
}

/* background-image, not the `background` shorthand: the shorthand would reset
   the light rule's background-size and kill the shimmer. */
.dark-mode .rev-skel {
  background-image: linear-gradient(
    90deg,
    var(--app-elevated) 25%,
    var(--app-border-strong) 37%,
    var(--app-elevated) 63%
  );
}

/* ---- Footer ---- */
.dark-mode .rev-footer {
  background: var(--app-card);
}

.dark-mode .rev-btn--primary {
  color: var(--app-accent-fg);
}

/* ============================================================================
   END DARK MODE
   ============================================================================ */

/* Tailwind-flavoured palette: slate neutrals + HRDC blue accent */
.rev-card {
  --blue-400: #38bdf8;
  --blue-500: #0ea5e9;
  --blue-600: #0284c7;
  --blue-700: #0369a1;
  --slate-50: #f8fafc;
  --slate-100: #f1f5f9;
  --slate-200: #e2e8f0;
  --slate-400: #94a3b8;
  --slate-500: #64748b;
  --slate-700: #334155;
  --slate-800: #1e293b;

  background: var(--slate-50);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 84vh;
  font-family: inherit;
  box-shadow: 0 24px 60px -12px rgba(3, 105, 161, 0.26);
}

/* ===== Header ===== */
.rev-header {
  position: relative;
  padding: 22px 22px 18px;
  background: linear-gradient(135deg, #0369a1 0%, #0284c7 55%, #0ea5e9 100%);
  overflow: hidden;
}
.rev-header__decor {
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
  pointer-events: none;
}
.rev-header__decor--1 {
  width: 180px;
  height: 180px;
  top: -90px;
  right: -40px;
  background: rgba(255, 255, 255, 0.13);
}
.rev-header__decor--2 {
  width: 120px;
  height: 120px;
  bottom: -70px;
  left: 30%;
  background: rgba(255, 255, 255, 0.08);
}
.rev-header__bar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
}
.rev-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  backdrop-filter: blur(4px);
}
.rev-header__text {
  flex: 1;
  min-width: 0;
}
.rev-header__title {
  font-size: 1.18rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
  line-height: 1.15;
  letter-spacing: -0.2px;
}
.rev-header__subtitle {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.82);
  margin: 2px 0 0;
}
.rev-ghost-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  transition: all 0.18s ease;
  flex-shrink: 0;
}
.rev-ghost-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.28);
}
.rev-ghost-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.rev-spin {
  animation: rev-spin 0.8s linear infinite;
}
@keyframes rev-spin {
  to { transform: rotate(360deg); }
}

/* Stat chips */
.rev-stats {
  position: relative;
  display: flex;
  gap: 10px;
  margin-top: 18px;
}
.rev-stat {
  flex: 1;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 13px;
  padding: 10px 14px;
  backdrop-filter: blur(6px);
}
.rev-stat--muted {
  background: rgba(255, 255, 255, 0.07);
}
.rev-stat__num {
  display: block;
  font-size: 1.45rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.5px;
}
.rev-stat__label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 5px;
}

/* ===== Toolbar / Search ===== */
.rev-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px 0;
  background: var(--slate-50);
}

/* Tab switcher (segmented control) */
.rev-tabs {
  display: inline-flex;
  flex-shrink: 0;
  gap: 2px;
  padding: 3px;
  background: #fff;
  border: 1px solid var(--slate-200);
  border-radius: 12px;
}
.rev-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 9px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--slate-500);
  transition: all 0.18s ease;
}
.rev-tab:hover {
  background: var(--slate-100);
}
.rev-tab--active,
.rev-tab--active:hover {
  background: var(--blue-500);
  color: #fff;
}

/* Calendar month navigation */
.rev-cal-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.rev-cal-nav__btn {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid var(--slate-200);
  transition: all 0.18s ease;
}
.rev-cal-nav__btn:hover {
  background: var(--slate-100);
}
.rev-cal-nav__title {
  min-width: 132px;
  text-align: center;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--slate-700);
}
.rev-cal-today {
  margin-left: 4px;
  padding: 7px 14px;
  border-radius: 9px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--blue-600);
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  transition: all 0.18s ease;
}
.rev-cal-today:hover {
  background: #e0f2fe;
}

.rev-search {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  background: #fff;
  border: 1px solid var(--slate-200);
  border-radius: 12px;
  padding: 0 12px;
  height: 42px;
  transition: all 0.18s ease;
}
.rev-search:focus-within {
  border-color: var(--blue-500);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.rev-search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.86rem;
  color: var(--slate-800);
}
.rev-search__input::placeholder {
  color: var(--slate-400);
}
.rev-search__clear {
  display: flex;
  align-items: center;
}

/* ===== Body ===== */
.rev-body {
  padding: 16px 22px 20px;
  overflow-y: auto;
  flex: 1;
}

/* ===== Calendar view ===== */
.rev-cal-wrap {
  height: 460px;
  background: #fff;
  border: 1px solid var(--slate-200);
  border-radius: 14px;
  overflow: hidden;
}
.rev-cal-wrap >>> .v-calendar-weekly__head-weekday {
  padding: 8px 4px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: var(--slate-400);
  border-color: var(--slate-100);
}
.rev-cal-wrap >>> .v-calendar-weekly__day {
  border-color: var(--slate-100);
}
.rev-cal-wrap >>> .v-calendar-weekly__day-label .v-btn {
  font-size: 0.76rem;
  font-weight: 600;
}
.rev-cal-wrap >>> .v-event {
  border-radius: 6px !important;
  font-size: 0.72rem;
  font-weight: 600;
  margin-bottom: 2px;
}
.rev-cal-wrap >>> .v-calendar-weekly__day.v-present .v-calendar-weekly__day-label .v-btn {
  background: var(--blue-500) !important;
  color: #fff !important;
}

/* Event detail popover.
   NOTE: v-menu teleports this out of .rev-card, so the local slate/blue scale
   does not resolve here — these rules use --app-* tokens directly, which also
   makes them theme-aware without a .dark-mode companion. */
.rev-evt-pop {
  display: flex;
  width: 270px;
  background: var(--app-card);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px -8px var(--app-shadow-deep);
}
.rev-evt-pop__bar {
  width: 4px;
  flex-shrink: 0;
}
.rev-evt-pop__main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  min-width: 0;
}
.rev-evt-pop__module {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--app-text-muted);
}
.rev-evt-pop__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--app-text);
  line-height: 1.25;
}
.rev-evt-pop__dates {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.76rem;
  color: var(--app-text-muted);
}

/* "X more" day popover — teleported too, see the note above. */
.rev-more-pop {
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
  background: var(--app-card);
  border-radius: 12px;
  box-shadow: 0 12px 32px -8px var(--app-shadow-deep);
}
.rev-more-pop__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border);
  position: sticky;
  top: 0;
  background: var(--app-card);
}
.rev-more-pop__list {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rev-more-pop__item {
  display: flex;
  gap: 8px;
  padding: 8px;
  border-radius: 9px;
  background: var(--app-elevated);
}
.rev-more-pop__bar {
  width: 4px;
  border-radius: 4px;
  flex-shrink: 0;
}
.rev-more-pop__main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.rev-more-pop__module {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--app-text-muted);
}
.rev-more-pop__title {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--app-text);
  line-height: 1.25;
}
.rev-more-pop__dates {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  color: var(--app-text-muted);
}
.rev-more-pop__empty {
  padding: 16px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--app-text-muted);
}

/* ===== Module card ===== */
.rev-module {
  position: relative;
  background: #fff;
  border: 1px solid var(--slate-200);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.rev-module:last-child {
  margin-bottom: 0;
}
.rev-module:not(.rev-module--idle)::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--accent, #0ea5e9);
}
.rev-module:not(.rev-module--idle):hover {
  box-shadow: 0 8px 24px -6px rgba(15, 23, 42, 0.16);
  transform: translateY(-1px);
}
.rev-module--idle {
  background: var(--slate-50);
  border-style: dashed;
  box-shadow: none;
}
.rev-module__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rev-module__icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #f0f9ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rev-module--idle .rev-module__icon {
  background: var(--slate-100);
}
.rev-module__name {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--slate-800);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rev-module--idle .rev-module__name {
  color: var(--slate-400);
  font-weight: 600;
}
.rev-module__count {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}
.rev-module__count--on {
  background: #ecfdf5;
  color: #059669;
}
.rev-module__count--off {
  background: var(--slate-100);
  color: var(--slate-400);
}
.rev-module__idle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  color: var(--slate-400);
  padding: 8px 2px 2px;
}

/* ===== Event rows ===== */
.rev-events {
  margin-top: 10px;
  border-top: 1px solid var(--slate-100);
}
.rev-event {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--slate-100);
}
.rev-event:last-child {
  border-bottom: none;
  padding-bottom: 2px;
}
.rev-event__title {
  flex: 1;
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--slate-700);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rev-event__dates {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: var(--slate-500);
  white-space: nowrap;
  flex-shrink: 0;
}
/* ===== States ===== */
.rev-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 44px 20px;
  gap: 4px;
}
.rev-state__icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid var(--slate-200);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.rev-state--error .rev-state__icon {
  background: #fef2f2;
  border-color: #fecaca;
}
.rev-state__title {
  font-size: 0.96rem;
  font-weight: 700;
  color: var(--slate-700);
}
.rev-state__text {
  font-size: 0.8rem;
  color: var(--slate-400);
  margin-bottom: 14px;
}

/* ===== Loading skeleton ===== */
.rev-skel-card {
  background: #fff;
  border: 1px solid var(--slate-200);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
}
.rev-skel-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.rev-skel {
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
  background-size: 400% 100%;
  animation: rev-shimmer 1.4s ease infinite;
}
.rev-skel--chip {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  flex-shrink: 0;
}
.rev-skel--title {
  height: 14px;
  width: 45%;
}
.rev-skel--line {
  height: 11px;
  width: 100%;
  margin-bottom: 10px;
}
.rev-skel--short {
  width: 65%;
  margin-bottom: 0;
}
@keyframes rev-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

/* ===== Footer ===== */
.rev-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 22px;
  border-top: 1px solid var(--slate-200);
  background: #fff;
}
.rev-footer__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  color: var(--slate-400);
}
.rev-btn {
  font-size: 0.82rem;
  font-weight: 700;
  padding: 9px 20px;
  border-radius: 11px;
  transition: all 0.18s ease;
}
.rev-btn--primary {
  color: #fff;
  background: var(--blue-500);
}
.rev-btn--primary:hover {
  background: var(--blue-600);
}

/* ===== Responsive ===== */
@media (max-width: 560px) {
  .rev-stats {
    flex-wrap: wrap;
  }
  .rev-stat {
    flex-basis: calc(50% - 5px);
  }
  .rev-event {
    flex-wrap: wrap;
    gap: 6px;
  }
  .rev-event__title {
    flex-basis: 100%;
  }

  /* Toolbar can't fit tabs + month nav on one line: stack them */
  .rev-toolbar {
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 16px 0;
  }
  .rev-tabs {
    width: 100%;
  }
  .rev-tab {
    flex: 1;
    justify-content: center;
  }
  .rev-cal-nav {
    width: 100%;
    margin-left: 0;
  }
  .rev-cal-nav__btn {
    flex-shrink: 0;
  }
  .rev-cal-nav__title {
    flex: 1;
    min-width: 0;
  }
  .rev-cal-today {
    flex-shrink: 0;
  }
  .rev-body {
    padding: 14px 16px 18px;
  }
}
</style>

<!-- Non-scoped: the v-dialog wrapper is teleported out of this component, so
     it can't be reached by scoped styles. Strip its default white background,
     radius and elevation so only our rounded card shows (no ghost border). -->
<style>
.rev-dialog.v-dialog {
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 20px;
}
</style>
